import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSidebar } from "@/domains/core/layout/useSidebar";
import { useSearch } from "@/shared/contexts";
import { cn } from "@/shared/utils";
import { ChevronRight, Search, Bell, Settings, LogOut, X } from "lucide-react";
import { navigationConfig } from "./config";
import { SearchResults } from "@/components/molecules/SearchResults";
import type { NavItem } from "./types";

// Halbkreis-Menü Komponente
const ArcMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);

  const { setIsSearchOpen } = useSearch();

  const menuItems = [
    {
      icon: Search,
      label: "Search",
      href: "#",
      onClick: () => setIsSearchOpen(true),
    },
    { icon: Bell, label: "Notifications", href: "/notifications" },
    { icon: Settings, label: "Settings", href: "/settings" },
    { icon: LogOut, label: "Logout", href: "/logout" },
  ];

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative">
      {/* Menu Toggle Button */}
      <button
        onClick={toggleMenu}
        className="group relative w-12 h-12 rounded-xl bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/50 backdrop-blur-sm hover:from-slate-700/70 hover:to-slate-600/70 hover:border-slate-500/70 hover:shadow-lg hover:shadow-slate-900/20 active:scale-95 transition-all duration-300 flex items-center justify-center"
        aria-label="Open menu"
      >
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl" />

        {/* Hamburger Icon */}
        <div className="relative z-10 flex flex-col items-center justify-center space-y-1">
          <div
            className={cn(
              "w-4 h-0.5 bg-slate-300 transition-all duration-300",
              isOpen ? "rotate-45 translate-y-1.5" : "",
            )}
          />
          <div
            className={cn(
              "w-4 h-0.5 bg-slate-300 transition-all duration-300",
              isOpen ? "opacity-0" : "",
            )}
          />
          <div
            className={cn(
              "w-4 h-0.5 bg-slate-300 transition-all duration-300",
              isOpen ? "-rotate-45 -translate-y-1.5" : "",
            )}
          />
        </div>

        {/* Subtle Glow Effect */}
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </button>

      {/* Vertical Menu Overlay - Nach oben öffnend */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />

          {/* Vertical Menu - Nach oben öffnend */}
          <div className="fixed bottom-6 left-0 z-50">
            {/* Menu Container */}
            <div className="relative">
              {/* Menu Background */}
              <div className="relative w-48 bg-gradient-to-t from-slate-800/95 via-slate-700/95 to-slate-600/95 backdrop-blur-xl rounded-t-2xl border border-slate-600/50 shadow-2xl shadow-slate-900/50">
                {/* Menu Items - Vertikal angeordnet */}
                <div className="flex flex-col p-2">
                  {menuItems.map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                      <button
                        key={item.label}
                        onClick={item.onClick || (() => (window.location.href = item.href))}
                        className="group/item flex items-center space-x-3 p-3 rounded-lg transition-all duration-300 hover:bg-slate-600/30 hover:scale-[1.02] w-full text-left"
                        style={{
                          animationDelay: `${index * 100}ms`,
                          animation: isOpen ? "slideUpAppear 0.4s ease-out forwards" : "none",
                        }}
                      >
                        {/* Icon Container */}
                        <div className="relative w-8 h-8 flex items-center justify-center">
                          <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-slate-600/30 to-slate-500/20 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300" />
                          <IconComponent className="w-5 h-5 text-slate-300 group-hover/item:text-white transition-colors duration-200 relative z-10" />
                        </div>

                        {/* Label */}
                        <span className="text-sm font-medium text-slate-300 group-hover/item:text-white transition-colors duration-200">
                          {item.label}
                        </span>

                        {/* Hover Glow */}
                        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover/item:opacity-100 transition-opacity duration-300" />
                      </button>
                    );
                  })}

                  {/* Separator */}
                  <div className="mx-3 my-2 h-px bg-slate-600/50" />

                  {/* Close Button */}
                  <button
                    onClick={() => setIsOpen(false)}
                    className="flex items-center justify-center w-full p-3 rounded-lg transition-all duration-300 hover:bg-slate-600/30 hover:scale-[1.02]"
                  >
                    <div className="relative w-8 h-8 flex items-center justify-center">
                      <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-slate-600/30 to-slate-500/20 opacity-0 hover:opacity-100 transition-opacity duration-300" />
                      <X className="w-5 h-5 text-slate-300 hover:text-white transition-colors duration-200 relative z-10" />
                    </div>
                    <span className="ml-3 text-sm font-medium text-slate-300 hover:text-white transition-colors duration-200">
                      Close
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Search Modal for Collapsed Sidebar */}
      <SearchModal />

      {/* CSS Animation */}
      <style>{`
        @keyframes slideUpAppear {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

// Search Modal Component for Collapsed Sidebar
const SearchModal: React.FC = () => {
  const { isSearchOpen, searchQuery, setSearchQuery, performSearch, clearSearch } = useSearch();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    performSearch(value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      clearSearch();
    }
  };

  if (!isSearchOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" onClick={clearSearch} />

      {/* Search Modal */}
      <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-96">
        <div className="bg-slate-800/95 backdrop-blur-xl border border-slate-600/50 rounded-2xl shadow-2xl shadow-slate-900/50">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-slate-600/30">
            <div className="flex items-center space-x-2">
              <Search className="w-5 h-5 text-slate-300" />
              <span className="text-lg font-semibold text-slate-200">Search</span>
            </div>
            <button
              onClick={clearSearch}
              className="p-1 rounded-lg hover:bg-slate-700/50 transition-colors duration-200"
            >
              <X className="w-5 h-5 text-slate-400 hover:text-white" />
            </button>
          </div>

          {/* Search Input */}
          <div className="p-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search here..."
                value={searchQuery}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                autoFocus
                className="w-full h-12 rounded-xl border border-slate-600/50 bg-slate-700/30 backdrop-blur-sm pl-4 pr-12 text-sm text-slate-200 placeholder-slate-400 outline-none focus:border-blue-500/50 focus:ring-2 focus:ring-blue-500/20 focus:bg-slate-700/50 transition-all duration-300"
              />
              <button
                onClick={() => performSearch(searchQuery)}
                className="absolute top-1/2 right-4 -translate-y-1/2 text-slate-400 hover:text-blue-400 transition-all duration-200 hover:scale-110"
              >
                <Search className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Search Results */}
          <div className="max-h-80">
            <SearchResults className="relative top-0 left-0 right-0 mt-0 shadow-none border-0 rounded-none" />
          </div>
        </div>
      </div>
    </>
  );
};

const CollapsedNavItem: React.FC<{ item: NavItem }> = ({ item }) => {
  const location = useLocation();
  const isActive =
    location.pathname === item.href ||
    (item.children &&
      item.children.some((child: NavItem) => {
        return child.href ? location.pathname.startsWith(child.href) : false;
      }));

  const content = (
    <div
      className={cn(
        "group relative flex items-center justify-center transition-all duration-300 cursor-pointer hover:bg-gradient-to-r hover:from-slate-700/30 hover:to-slate-600/20 hover:shadow-lg hover:shadow-slate-900/20 hover:scale-105 rounded-xl py-3 px-3 mx-2 mb-2",
        isActive && "bg-gradient-to-r from-blue-600/90 to-blue-500/80 shadow-lg shadow-blue-900/30",
      )}
    >
      {/* Active Glow Effect */}
      {isActive && (
        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/20 to-purple-500/20 blur-sm" />
      )}

      {/* Icon Container */}
      {item.icon && (
        <div className="relative flex-shrink-0 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 w-6 h-6">
          <div
            className={cn(
              "absolute inset-0 rounded-lg transition-all duration-300",
              isActive
                ? "bg-gradient-to-br from-white/20 to-white/10"
                : "group-hover:bg-gradient-to-br group-hover:from-slate-600/30 group-hover:to-slate-500/20",
            )}
          />
          <span className="relative z-10 flex items-center justify-center w-full h-full">
            {typeof item.icon === "function" ? <item.icon size="navbar" /> : item.icon}
          </span>
        </div>
      )}

      {/* Active Indicator */}
      {isActive && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-gradient-to-b from-blue-400 to-purple-400 rounded-l-full shadow-lg shadow-blue-400/50" />
      )}

      {/* Hover Glow */}
      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
    </div>
  );

  // If item has href, wrap in Link
  if (item.href) {
    return (
      <Link to={item.href} className="block">
        {content}
      </Link>
    );
  }

  // If no href, return as div
  return content;
};

export const CollapsedSidebar: React.FC = () => {
  const { toggle } = useSidebar();

  return (
    <aside
      className="h-full w-full border-r border-slate-700/50 flex flex-col shadow-2xl shadow-slate-900/50 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900"
      style={{
        minWidth: "80px",
        maxWidth: "80px",
        height: "100vh",
      }}
    >
      <div className="flex h-full w-full flex-col">
        {/* Top Section - Fixed */}
        <div className="flex-shrink-0">
          {/* User Profile with Toggle Button */}
          <div className="pt-8 px-4 pb-8">
            <div className="flex flex-col items-center space-y-8">
              {/* Avatar */}
              <div className="relative h-14 w-14 rounded-2xl overflow-hidden bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-blue-500/25 group-hover:shadow-xl group-hover:shadow-blue-500/40 transition-all duration-300 group-hover:scale-105">
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent" />
                <img
                  src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
                  alt="Profile"
                  className="relative z-10 h-full w-full object-cover"
                />
                <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-slate-900 shadow-lg" />
              </div>

              {/* Toggle Button - centered below avatar */}
              <button
                onClick={toggle}
                className="group/toggle relative overflow-hidden rounded-xl transition-all duration-300 bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600/50 backdrop-blur-sm hover:from-slate-700/70 hover:to-slate-600/70 hover:border-slate-500/70 hover:shadow-lg hover:shadow-slate-900/20 active:scale-95 p-2"
                aria-label="Expand sidebar"
              >
                {/* Animated Background */}
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 opacity-0 group-hover/toggle:opacity-100 transition-opacity duration-300" />

                {/* Icon Container */}
                <div className="relative z-10 flex items-center justify-center">
                  <ChevronRight className="w-4 h-4 text-slate-300 group-hover/toggle:text-white transition-colors duration-200" />
                </div>

                {/* Subtle Glow Effect */}
                <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover/toggle:opacity-100 transition-opacity duration-300" />
              </button>
            </div>
          </div>
        </div>

        {/* Navigation Sections - No internal scrolling */}
        <div className="flex-1">
          {navigationConfig.sections.map((section) => (
            <div key={section.id} className="mb-8">
              {/* Section Items */}
              <nav className="px-3">
                <ul className="space-y-2">
                  {section.items.map((item: NavItem) => (
                    <li key={item.id}>
                      <CollapsedNavItem item={item} />
                    </li>
                  ))}
                </ul>
              </nav>
            </div>
          ))}
        </div>

        {/* Bottom Section - Fixed with consistent spacing */}
        <div className="flex-shrink-0 m-6 mt-8 border-t border-slate-700/30 pt-6 pb-6">
          {/* Arc Menu */}
          <div className="flex justify-center">
            <ArcMenu />
          </div>
        </div>
      </div>
    </aside>
  );
};
